"use client";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import type { MouseEvent } from "react";
import type { Song } from "~/types";
import type { ActionsVariant } from "./songActionsFactory";
import SongArtwork from "./SongArtwork";
import { useAudioControls, useAudioCurrentState } from "./useAudio";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";

interface SongListItemProps {
  song: Song;
  playlist: Song[];
  actionsVariant?: ActionsVariant;
}

export default function SongListItem({
  song,
  playlist,
  actionsVariant,
}: SongListItemProps) {
  const { playSong, pause, resume, open } = useAudioControls();
  const { isCurrent, isCurrentPlaying } = useAudioCurrentState(song.id);

  const handlePlayPause = (e: MouseEvent) => {
    e.stopPropagation();
    if (isCurrentPlaying) {
      pause();
    } else if (isCurrent) {
      resume();
    } else {
      playSong(song, playlist, actionsVariant);
      open();
    }
  };

  return (
    <ListItem
      onClick={handlePlayPause}
      sx={{
        cursor: "pointer",
        borderRadius: 2,
        "&:hover": {
          bgcolor: "action.hover",
        },
        "&:hover .artworkOverlay": {
          opacity: 1,
          pointerEvents: "auto",
          "& .playButton:hover": {
            transform: "scale(1.2)",
            bgcolor: "rgba(0,0,0,0.6)",
          },
        },
        px: 1,
        py: 0.5,
      }}
      disablePadding={false}
    >
      {/* Artwork */}
      <Box
        sx={{
          position: "relative",
          borderRadius: 1,
          overflow: "hidden",
          flexShrink: 0,
          bgcolor: "action.selected",
          mr: 2,
        }}
      >
        <SongArtwork song={song} size={56} />
        {/* Overlay with play/pause visible on list item hover */}
        <Box
          className="artworkOverlay"
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.35)",
            opacity: 0,
            transition: "opacity 200ms",
            pointerEvents: "none",
          }}
        >
          <IconButton
            className="playButton"
            onClick={handlePlayPause}
            aria-label={isCurrentPlaying ? "Oprește" : "Redă"}
            size="small"
            sx={{
              color: "common.white",
              bgcolor: "rgba(0,0,0,0.45)",
            }}
          >
            {isCurrentPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Box>
      </Box>
      {/* Text */}
      <ListItemText
        primary={
          <Typography
            variant="body1"
            color={isCurrent ? "primary.main" : "text.primary"}
            noWrap
            sx={{
              fontWeight: isCurrent ? 700 : 400
            }}
          >
            {song.title}
          </Typography>
        }
        secondary={
          song.description ? (
            <Typography variant="body2" noWrap sx={{
              color: "text.secondary"
            }}>
              {song.description}
            </Typography>
          ) : undefined
        }
        sx={{ flex: 1, overflow: "hidden" }}
      />
    </ListItem>
  );
}
